/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState } from 'react';
import Image from 'next/image';

import Button from '@/lib/Button';
import Modal from '@/lib/Modal';
import Icon from '@/lib/Icon/Icon';
import Accordion from '@/lib/Accordian';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';
import { useGlobalStore } from '@/store';

type EmiPlansFailureProps = {
  isOpen: boolean;
  onDismiss: () => void;
  onClick: (val: string) => void;
};

const FAILURE_REASONS = [
  {
    text: 'Low Credit Score',
    img: '/credit_score.svg',
  },
  {
    text: 'Document mismatch',
    img: '/document.svg',
  },
  {
    text: 'Limited credit history',
    img: '/history.svg',
  },
  {
    text: 'Unserviceable location',
    img: '/location.svg',
  },
  {
    text: 'Under minimum age',
    img: '/age.svg',
  },
];

const EmiPlansFailure = ({ isOpen, onDismiss, onClick }: EmiPlansFailureProps): JSX.Element => {
  const [showFailureReason, setShowFailureReason] = useState(false);
  const { activeView, user, userAccessToken } = useGlobalStore();

  const expandProvider = (): void => {
    setShowFailureReason(!showFailureReason);
  };

  const handleEditPan = (): void => {
    onClick('edit_pan');
    // Track Edit Pan Click
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.OFFER_EDIT_PAN,
      screen: activeView,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      },
    });
  };

  const handleChangePayment = (): void => {
    onClick('cancel');
    // Track Edit Pan Click
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.OFFER_EDIT_PAN,
      screen: activeView,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      },
    });
  };

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss}>
      <Modal.Body className="d:flex-col d:items-center d:justify-center">
        <div className="d:w-1/2 d:flex d:flex-col d:items-center">
          <div
            data-testid="emi-plans-error"
            className="mb-7 p-3 bg-surface-negative-faded inline-block rounded-md border border-negative-subtle"
          >
            <Icon width={24} fill="#D92D20" name="filledWarning" />
          </div>
          <h2 className="font-tasa font-semibold mt-0 text-on-grey-normal text-2xl d:text-center">
            Offers unavailable
          </h2>
          <p className="mt-2 mb-4 text-on-grey-subtle text-sm">
            You can edit your PAN details or choose another payment method
          </p>
          <Accordion
            isOpen={showFailureReason}
            onClick={expandProvider}
            heading="Check for possible reasons for unavailability"
          >
            <div
              data-tastid="emi-failure-reason"
              className="mt-6 rounded-xl bg-surface-grey-subtle flex px-5 py-4 flex flex-wrap"
            >
              {FAILURE_REASONS.map((reason, index) => {
                return (
                  <div
                    className={`w-5/12 flex items-center ${index !== 4 ? ' mb-4' : ''} ${
                      index % 2 === 0 ? ' mr-6' : ''
                    }`}
                    key={index}
                  >
                    <div className="bg-primary-intense bg-opacity-9 min-w-8 min-h-8 p-1.5 rounded">
                      <Image width={20} height={20} src={reason.img} alt="Failure Image" />
                    </div>
                    <div className="text-black-subtle text-xs ml-3 font-medium">{reason.text}</div>
                  </div>
                );
              })}
            </div>
          </Accordion>
          <Button onClick={handleChangePayment}>Change payment method</Button>
          <Button onClick={handleEditPan} className="mt-0" variant="secondary">
            Edit PAN details
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default EmiPlansFailure;
